/** @format */

import moment from 'moment';
import i18next from 'i18next';
import { ExportToCsv } from 'export-to-csv';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import Order from '../../../interfaces/forms/Order';
import Filter from '../../../interfaces/forms/Filter';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import { PlusOrdersCommonResponse } from '../../../interfaces/platform/PlusOrdersCommonResponse';
import { DateHelperTs } from '../../../helpers/DateHelperTs';
import { PlatformResellerCollectionInterface } from '../../../interfaces/collections/PlatformResellerCollectionInterface';
import { MvnoBlockListCollectionInterface } from '../../../interfaces/collections/MvnoBlockListCollectionInterface';

const _ = require('underscore');

interface PlkListToCsvData {
    id: number;
    platformResellerId: string | null;
    mvnoBlockRequestId: string | number | null;
    mvnoBlockListId: string | null;
    command: string | null;
    msisdn: string | number;
    imsi: string | number;
    requestTime: string | null;
    responseTime: string | null;
    errorStatus: string | null;
}

export class PlkListToCsv {
    private readonly itemLoader: ItemLoaders;
    private readonly platformResellerList: PlatformResellerCollectionInterface;
    private readonly mvnoBlockList: MvnoBlockListCollectionInterface;

    constructor(itemLoader: ItemLoaders, platformResellerList: PlatformResellerCollectionInterface, mvnoBlockList: MvnoBlockListCollectionInterface) {
        this.itemLoader = itemLoader;
        this.platformResellerList = platformResellerList;
        this.mvnoBlockList = mvnoBlockList;
    }

    exportToCsv = (orders: Array<Order>, filters: Array<Filter>, showMainLoader: (show: boolean) => void): void => {
        const extendFilter = _.clone(filters);
        extendFilter.push({ name: 'pagination', value: 'false' });
        showMainLoader(true);
        this.itemLoader.getListWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/orders_common_responses')),
            'plk_list_loader',
            extendFilter,
            orders,
            (items: { 'hydra:member': Array<PlusOrdersCommonResponse>; 'hydra:totalItems': number }): void => {
                this.prepareCsv(items);
                showMainLoader(false);
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    };

    prepareCsv = (items: { 'hydra:member': Array<PlusOrdersCommonResponse>; 'hydra:totalItems': number }): void => {
        const csvList = this.prepareCsvData(items);
        const options = {
            filename: 'operation_mno' + moment().format('YYYYMMDD[_]HHmm'),
            fieldSeparator: ';',
            quoteStrings: '"',
            decimalSeparator: '.',
            showLabels: true,
            showTitle: false,
            title: 'MNO operation',
            useTextFile: false,
            useBom: true,
            useKeysAsHeaders: false,
            headers: [
                i18next.t('prov:list.id'),
                i18next.t('prov:list.partner'),
                i18next.t('prov:list.block_request_id'),
                i18next.t('prov:list.block'),
                i18next.t('prov:list.operation'),
                i18next.t('prov:list.msisdn'),
                i18next.t('prov:list.imsi'),
                i18next.t('prov:list.request_date'),
                i18next.t('prov:list.response_date'),
                i18next.t('prov:list.status'),
            ],
        };
        const exportCsv = new ExportToCsv(options);
        exportCsv.generateCsv(csvList);
    };

    getPlatformResellerName = (platformResellerId: number): string | null => {
        for (let i = 0; i < this.platformResellerList['hydra:member'].length; i++) {
            if (this.platformResellerList['hydra:member'][i].id === platformResellerId) {
                return this.platformResellerList['hydra:member'][i].mainname;
            }
        }
        return null;
    };

    getMvnoBlockListName = (mvnoBlockListNameId: number): string | null => {
        for (let i = 0; i < this.mvnoBlockList['hydra:member'].length; i++) {
            if (this.mvnoBlockList['hydra:member'][i].id === mvnoBlockListNameId) {
                return this.mvnoBlockList['hydra:member'][i].blockName;
            }
        }
        return null;
    };

    prepareCsvData = (items: { 'hydra:member': Array<PlusOrdersCommonResponse>; 'hydra:totalItems': number }): Array<PlkListToCsvData> => {
        const csvList: Array<PlkListToCsvData> = [];
        if (items['hydra:member'] && items['hydra:totalItems'] && items['hydra:totalItems'] > 0) {
            items['hydra:member'].map((item: PlusOrdersCommonResponse) => {
                let errorStatus = 'ok';
                if (item.errorStatus) {
                    errorStatus = 'error';
                }
                csvList.push({
                    id: item.id,
                    platformResellerId: this.getPlatformResellerName(item.platformResellerId),
                    mvnoBlockRequestId: item.mvnoBlockRequestId,
                    mvnoBlockListId: this.getMvnoBlockListName(item.mvnoBlockListId),
                    command: item.command !== 'REST_API_CALL' ? item.command : 'GEN/GMLC/ApiRest',
                    msisdn: item.msisdn,
                    imsi: item.imsi,
                    requestTime: DateHelperTs.format(item.requestTime),
                    responseTime: DateHelperTs.format(item.responseTime),
                    errorStatus: i18next.t('prov:state.' + errorStatus),
                });
            });
        }
        return csvList;
    };
}
